'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_HOTELS = [
  { name: 'Burj Al Arab', location: 'Dubai, UAE', type: 'Iconic Sail-shaped Tower', price: 'From $1,800/night', tags: ['Ultra-luxury', 'Iconic', 'Butler Service'] },
  { name: 'Aman Tokyo', location: 'Tokyo, Japan', type: 'Urban Sanctuary', price: 'From $1,200/night', tags: ['Zen', 'Spa', 'City Views'] },
  { name: 'Four Seasons Bora Bora', location: 'French Polynesia', type: 'Overwater Bungalows', price: 'From $2,400/night', tags: ['Overwater', 'Lagoon', 'Honeymoon'] },
  { name: 'The Ritz Paris', location: 'Paris, France', type: 'Historic Palace Hotel', price: 'From $1,600/night', tags: ['Historic', 'Iconic', 'Haute Cuisine'] },
  { name: 'One&Only Reethi Rah', location: 'Maldives', type: 'Private Island Resort', price: 'From $2,800/night', tags: ['Private Island', 'Overwater', 'Spa'] },
  { name: 'Singita Grumeti', location: 'Tanzania', type: 'Luxury Safari Lodge', price: 'From $3,200/night', tags: ['Safari', 'Wildlife', 'All-inclusive'] },
];

const HOTEL_TYPES = ['All', 'City Hotels', 'Beach Resorts', 'Safari Lodges', 'Mountain Retreats', 'Private Islands', 'Historic Palaces'];

export default function HotelsPage() {
  const router = useRouter();
  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [activeType, setActiveType] = useState('All');

  const handleSearch = () => {
    const params = new URLSearchParams({
      vertical: 'hotel',
      destination,
      checkIn,
      checkOut,
      travelers: String(guests),
    });
    router?.push(`/booking?${params?.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
          <Link href="/activities" className="hover:text-[var(--primary)] transition-colors">Activities</Link>
          <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas & Estates</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Hotels & Residences</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Sleep Somewhere<br />
            <span className="gold-text-gradient">Unforgettable.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-10 max-w-xl">
            Curated palaces, villas, and design hotels around the world. Every property personally vetted by our specialists.
          </p>

          {/* Search Widget */}
          <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.15)]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="lg:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Destination</label>
                <input
                  type="text"
                  placeholder="City, hotel or region"
                  value={destination}
                  onChange={(e) => setDestination(e?.target?.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Check In</label>
                <input type="date" value={checkIn} onChange={(e) => setCheckIn(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Check Out</label>
                <input type="date" value={checkOut} onChange={(e) => setCheckOut(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Guests</label>
                <select value={guests} onChange={(e) => setGuests(Number(e?.target?.value))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5, 6, 8, 10]?.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Rooms</label>
                <select value={rooms} onChange={(e) => setRooms(Number(e?.target?.value))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5]?.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Room' : 'Rooms'}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleSearch} className="btn-primary w-full py-3 rounded-xl text-sm font-semibold">Search Hotels</button>
          </div>
        </div>
      </section>
      {/* Hotel Types */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {HOTEL_TYPES?.map((t) => (
              <button key={t} onClick={() => setActiveType(t)} className={`flex-shrink-0 px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${activeType === t ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'}`}>{t}</button>
            ))}
          </div>

          <p className="label-caps mb-2">Featured Properties</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Curated <span className="gold-text-gradient">Luxury Properties</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURED_HOTELS?.map((h) => (
              <div key={h?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={handleSearch}>
                <div className="text-2xl mb-3">🏨</div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{h?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{h?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3">{h?.type}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {h?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{h?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Villas CTA */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-card rounded-2xl p-8 border border-[rgba(201,168,76,0.2)] flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="label-caps mb-2 text-[var(--primary)]">Whole-house rentals</p>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Prefer a Private Villa?</h3>
              <p className="text-sm text-[var(--muted-foreground)] max-w-md">Fully staffed private villas, historic estates and beachfront residences worldwide — butler, chef, housekeeping included.</p>
            </div>
            <Link href="/villas" className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold whitespace-nowrap">Explore Villas →</Link>
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas & Estates</Link>
            <Link href="/ai-concierge" className="hover:text-[var(--primary)] transition-colors">AI Concierge</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
